import React, { useState, useEffect } from "react";
import { listStaff, createStaff, updateStaff, deleteStaff, addAdvance, markAttendance, getSlip } from "../lib/api";
import { useToast } from "../hooks/use-toast";
import { BRAND } from "../mock/mock";
import { Plus, Trash2, IndianRupee, CalendarCheck, FileText, X, UserPlus } from "lucide-react";

const money = (n) => "\u20b9" + (Number(n) || 0).toLocaleString("en-IN");
const todayMonth = () => new Date().toISOString().slice(0, 7);

function printSlip(slip) {
  const logo = `${window.location.origin}/brand/viimii-word-dark.png`;
  const monthLabel = (() => {
    try { const [y, m] = slip.month.split("-"); return new Date(y, m - 1).toLocaleDateString("en-IN", { month: "long", year: "numeric" }); }
    catch { return slip.month; }
  })();
  const rows = (slip.advances || [])
    .map((a) => `<tr><td>${a.date}</td><td>${a.note || "Advance payment"}</td><td class="r">${money(a.amount)}</td></tr>`)
    .join("") || `<tr><td colspan="3" class="empty">No advance payments this month</td></tr>`;
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Salary Slip - ${slip.staff.name} - ${monthLabel}</title>
  <style>
    @page{margin:0}
    *{box-sizing:border-box}
    body{font-family:'Georgia','Times New Roman',serif;color:#3a2e25;margin:0;background:#f7f0e6}
    .sheet{max-width:720px;margin:0 auto;background:#fffdf9;min-height:100vh;padding:0 0 40px}
    .top{background:linear-gradient(135deg,#241812,#3a2418);color:#f4ece0;text-align:center;padding:34px 24px 26px;border-bottom:3px solid #c9a15a}
    .top img{height:46px;width:auto;filter:brightness(0) invert(1);opacity:.96}
    .tagline{letter-spacing:5px;font-size:10px;text-transform:uppercase;color:#e3cb95;margin-top:8px}
    .contact{font-size:11px;color:#cbb79a;margin-top:10px;line-height:1.7}
    .title{text-align:center;margin:26px 0 6px;font-size:26px;letter-spacing:2px;color:#b0863f}
    .subtitle{text-align:center;color:#8a7867;font-size:13px;margin-bottom:22px}
    .wrap{padding:0 34px}
    .box{border:1px solid #ecdfca;border-radius:12px;overflow:hidden;margin-bottom:18px}
    table{width:100%;border-collapse:collapse}
    .info td{padding:11px 16px;font-size:14px;border-bottom:1px solid #f0e7d6}
    .info td.k{color:#8a7867;text-transform:uppercase;font-size:10px;letter-spacing:1px;width:22%}
    .adv th{background:#faf4ea;text-align:left;padding:11px 16px;font-size:10px;letter-spacing:1px;text-transform:uppercase;color:#8a7867}
    .adv td{padding:11px 16px;font-size:13px;border-top:1px solid #f0e7d6}
    .adv td.r,.adv th.r{text-align:right}
    .empty{text-align:center;color:#b3a794;font-style:italic}
    .tot{display:flex;justify-content:space-between;padding:9px 16px;font-size:14px;color:#5a4a3a}
    .netbox{margin:8px 16px 0;background:linear-gradient(135deg,#f6ecd9,#efe0c6);border:1px solid #d9c19a;border-radius:12px;display:flex;justify-content:space-between;align-items:center;padding:16px 20px}
    .netbox .l{font-size:13px;text-transform:uppercase;letter-spacing:1px;color:#8a6a3a}
    .netbox .v{font-size:26px;font-weight:bold;color:#8a5a2a}
    .sign{display:flex;justify-content:space-between;margin:46px 34px 0}
    .sign div{width:40%;text-align:center;border-top:1px solid #cbb79a;padding-top:8px;font-size:12px;color:#8a7867}
    .foot{text-align:center;color:#b3a794;font-size:11px;margin-top:30px}
  </style></head><body>
  <div class="sheet">
    <div class="top">
      <img src="${logo}" alt="VIIMII STUDIO" />
      <div class="tagline">Nails \u00b7 Lashes \u00b7 Hair Spa</div>
      <div class="contact">${BRAND.address}<br/>${BRAND.phone} \u00b7 ${BRAND.instagramHandle} \u00b7 Rated ${BRAND.rating}\u2605 on Google</div>
    </div>
    <div class="title">SALARY SLIP</div>
    <div class="subtitle">For the month of ${monthLabel}</div>
    <div class="wrap">
      <div class="box"><table class="info">
        <tr><td class="k">Employee</td><td>${slip.staff.name}</td><td class="k">Role</td><td>${slip.staff.role || "-"}</td></tr>
        <tr><td class="k">Phone</td><td>${slip.staff.phone || "-"}</td><td class="k">Present Days</td><td>${slip.presentDays}</td></tr>
      </table></div>
      <div class="box"><table class="adv">
        <tr><th>Date</th><th>Description</th><th class="r">Amount</th></tr>
        ${rows}
      </table></div>
      <div class="tot"><span>Monthly Salary</span><span>${money(slip.salary)}</span></div>
      <div class="tot"><span>Less: Total Advances</span><span>- ${money(slip.totalAdvance)}</span></div>
      <div class="netbox"><span class="l">Net Payable</span><span class="v">${money(slip.net)}</span></div>
      <div class="sign"><div>Employee Signature</div><div>Authorised Signature</div></div>
      <div class="foot">This is a computer-generated salary slip - generated on ${new Date().toLocaleDateString("en-IN")} \u00b7 VIIMII STUDIO</div>
    </div>
  </div>
  <script>window.onload=function(){setTimeout(function(){window.print()},350)}<\/script>
  </body></html>`;
  const w = window.open("", "_blank");
  if (w) { w.document.write(html); w.document.close(); }
}

export default function StaffManager() {
  const { toast } = useToast();
  const [staff, setStaff] = useState([]);
  const [form, setForm] = useState({ name: "", role: "", phone: "", salary: "" });
  const [month, setMonth] = useState(todayMonth());
  const [advForm, setAdvForm] = useState({});

  const load = () => listStaff().then(setStaff).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name) { toast({ title: "Enter staff name" }); return; }
    await createStaff({ name: form.name, role: form.role, phone: form.phone, salary: Number(form.salary) || 0 });
    setForm({ name: "", role: "", phone: "", salary: "" });
    toast({ title: "Staff added" });
    load();
  };
  const remove = async (id) => { await deleteStaff(id); load(); };
  const saveAdvance = async (id) => {
    const a = advForm[id] || {};
    if (!a.amount) { toast({ title: "Enter advance amount" }); return; }
    await addAdvance(id, { amount: Number(a.amount), date: a.date || new Date().toISOString().slice(0, 10), note: a.note || "" });
    setAdvForm((f) => ({ ...f, [id]: {} }));
    toast({ title: "Advance recorded" });
  };
  const attendance = async (id) => {
    await markAttendance(id, new Date().toISOString().slice(0, 10), true);
    toast({ title: "Marked present today" });
  };
  const slip = async (id) => {
    try { const s = await getSlip(id, month); printSlip(s); }
    catch { toast({ title: "Could not generate slip" }); }
  };

  const inp = "bg-viimii border hairline rounded-lg px-3 py-2 text-sm text-cream placeholder-cream/35 focus:outline-none focus:border-gold";

  return (
    <div>
      {/* Add staff */}
      <div className="p-5 rounded-2xl border hairline bg-viimii-2 mb-6">
        <h3 className="font-display text-xl text-cream mb-4 flex items-center gap-2"><UserPlus size={18} className="text-gold" style={{ color: "var(--viimii-gold)" }} /> Add Staff Member</h3>
        <div className="grid sm:grid-cols-5 gap-3">
          <input className={inp} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className={inp} placeholder="Role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} />
          <input className={inp} placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <input className={inp} placeholder="Monthly Salary" type="number" value={form.salary} onChange={(e) => setForm({ ...form, salary: e.target.value })} />
          <button onClick={add} className="inline-flex items-center justify-center gap-2 text-black font-medium rounded-lg py-2" style={{ background: "var(--viimii-gold)" }}><Plus size={16} /> Add</button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-4">
        <label className="text-sm text-cream/60">Slip month</label>
        <input type="month" className={inp} value={month} onChange={(e) => setMonth(e.target.value)} />
      </div>

      {staff.length === 0 && <p className="text-cream/50 text-sm">No staff added yet.</p>}

      <div className="grid md:grid-cols-2 gap-5">
        {staff.map((s) => (
          <div key={s.id} className="p-5 rounded-2xl border hairline bg-viimii-2">
            <div className="flex items-start justify-between">
              <div>
                <h4 className="font-display text-xl text-cream">{s.name}</h4>
                <p className="text-sm text-cream/55">{s.role || "Staff"} {s.phone ? `\u00b7 ${s.phone}` : ""}</p>
                <p className="text-sm mt-1"><span className="text-cream/55">Salary:</span> <span className="text-gold" style={{ color: "var(--viimii-gold)" }}>{money(s.salary)}</span>/mo</p>
              </div>
              <button onClick={() => remove(s.id)} className="text-cream/40 hover:text-red-400"><Trash2 size={16} /></button>
            </div>
            <div className="mt-4 grid grid-cols-[1fr_auto_auto] gap-2 items-center">
              <input className={inp} placeholder="Advance amount" type="number" value={(advForm[s.id] || {}).amount || ""} onChange={(e) => setAdvForm((f) => ({ ...f, [s.id]: { ...(f[s.id] || {}), amount: e.target.value } }))} />
              <button onClick={() => saveAdvance(s.id)} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border hairline text-gold hover:bg-gold hover:text-black transition-colors"><IndianRupee size={13} /> Advance</button>
              <button onClick={() => attendance(s.id)} className="inline-flex items-center gap-1 text-xs px-3 py-2 rounded-lg border hairline text-cream/70 hover:text-gold"><CalendarCheck size={13} /> Present</button>
            </div>
            <button onClick={() => slip(s.id)} className="mt-3 w-full inline-flex items-center justify-center gap-2 text-sm text-black font-medium rounded-lg py-2.5" style={{ background: "var(--viimii-clay-soft)" }}>
              <FileText size={15} /> Generate Salary Slip ({month})
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
